import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FaArrowLeft, FaEdit, FaPlus,  FaTrash, FaTimes } from 'react-icons/fa';
import { TbDownload } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import connectionService from '../../../../services/community/connectionService';
import scheduleService from '../../../../services/community/scheduleService';
import {
  ScheduleCommunityModel,
  ScheduleModel,
  UpdateShiftBody,
} from '../../../../models/schedule.model';
import { Event, EventParticipationAnswer, RecurrenceEditType } from '../../../../types/event';
import { RootState } from '../../../../redux/reducers';
import { Connection, ConnectionType } from '../../../../redux/slices/connectionsSlice';
import ModalLayout from '../../../components/layouts/ModalLayout';
import PageWithTabsLayout from '../../../components/layouts/PageWithTabsLayout';
import Button from '../../../components/buttons/Button';
import Input from '../../../components/misc/Input';
import DatePicker from '../../../components/forms/DatePicker';
import Slider from '../../../components/misc/Slider';
import EmptyScreenView from '../../../components/misc/EmptyScreenView';
import ScheduleShiftsList from './ScheduleShiftsListTab';
import ScheduleWeekCalendar from './ScheduleWeekTab';
import FastAssigningTab from './FastAssigningTab';
import ScheduleCommunitiesTab from './ScheduleCommunitiesTab';
import ScheduleInsightsTab from './ScheduleInsightsTab';
import ScheduleConnectEventsTab from './ScheduleConnectEventsTab';
import { downloadScheduleShiftsExcel } from '../../../../utils/fileUtils';
import useIsMainBusinessCommunity from '../../../../hooks/business/useIsMainBusinessCommunity';
import ShiftCommunityAssignmentsEditor, {
  DraftShiftCommunityAssignment,
  getCommunityAssignmentsPayload,
  getTotalCommunityRequiredCount,
  mapShiftCommunityAssignments,
} from './ShiftCommunityAssignmentsEditor';
import { ShiftAssignmentMode } from './CreateShiftPage';

type SelectedAssignee = {
  key: string;
  type: ConnectionType;
  label: string;
  answer?: EventParticipationAnswer;
  userId?: number;
  externalMemberId?: number;
};

function getConnectionLabel(connection: Connection): string {
  return `${connection.fields.FIRST_NAME || ''} ${connection.fields.LAST_NAME || ''}`.trim();
}

function mapShiftAssignees(shift: Event): SelectedAssignee[] {
  return [
    ...shift.participants.map((participant) => ({
      key: `member-${participant.id}`,
      type: ConnectionType.MEMBER,
      label: `${participant.firstName} ${participant.lastName}`.trim(),
      answer: participant.answer,
      userId: participant.id,
    })),
    ...shift.externalInvites.map((invite) => ({
      key: `external-${invite.externalMemberId}`,
      type: ConnectionType.EXTERNAL_MEMBER,
      label: `${invite.firstName} ${invite.lastName}`.trim(),
      answer: invite.answer,
      externalMemberId: invite.externalMemberId,
    })),
  ];
}

function getShiftAllowSelfJoin(shift: Event): boolean {
  if (typeof shift.allowSelfJoin === 'boolean') {
    return shift.allowSelfJoin;
  }

  const { additionalData } = (shift as Event & { additionalData?: Record<string, unknown> });
  return additionalData?.allowSelfJoin === true;
}

function getShiftAllowShiftExchangeRequests(shift: Event): boolean {
  const shiftWithFlag = shift as Event & {
    allowShiftExchangeRequests?: boolean;
    additionalData?: Record<string, unknown>;
  };

  if (typeof shiftWithFlag.allowShiftExchangeRequests === 'boolean') {
    return shiftWithFlag.allowShiftExchangeRequests;
  }

  return shiftWithFlag.additionalData?.allowShiftExchangeRequests === true;
}

function updateShiftAssigneeAttendance(
  shift: Event,
  assignee: SelectedAssignee,
  answer: EventParticipationAnswer,
): Event {
  if (assignee.type === ConnectionType.MEMBER && assignee.userId) {
    return {
      ...shift,
      participants: shift.participants.map((participant) =>
        participant.id === assignee.userId ? { ...participant, answer } : participant,
      ),
    };
  }

  if (assignee.type === ConnectionType.EXTERNAL_MEMBER && assignee.externalMemberId) {
    return {
      ...shift,
      externalInvites: shift.externalInvites.map((invite) =>
        invite.externalMemberId === assignee.externalMemberId ? { ...invite, answer } : invite,
      ),
    };
  }

  return shift;
}

export default function ScheduleDetailsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const community = useSelectedCommunity();
  const connections = useSelector((state: RootState) => state.connections);
  const role = useSelector((state: RootState) => state.application.role);
  const isMainBusinessCommunity = useIsMainBusinessCommunity();

  type ViewKey = 'list' | 'calendar' | 'communities' | 'fastAssigning' | 'connectEvents' | 'insights';

  const viewLabels = useMemo(
    (): Record<ViewKey, string | undefined> => ({
      list: t('page.event.schedules.listView'),
      calendar: t('page.event.schedules.calendarView'),
      communities: isMainBusinessCommunity ? t('page.event.schedules.communities.tab') : undefined,
      fastAssigning: t('page.event.schedules.fastAssigning.tab'),
      connectEvents: t('page.event.schedules.connectEvents.tab'),
      insights: t('page.event.schedules.insights.tab'),
    }),
    [isMainBusinessCommunity, t],
  );

  const visibleTabs = useMemo(
    () => Object.values(viewLabels).filter((label): label is string => !!label),
    [viewLabels],
  );

  const [activeViewKey, setActiveViewKey] = useState<ViewKey>('list');

  const [schedule, setSchedule] = useState<ScheduleModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [shifts, setShifts] = useState<Event[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [selectedWeekStart, setSelectedWeekStart] = useState<dayjs.Dayjs>(() => {
    const today = dayjs();
    const daysToMonday = (today.day() + 6) % 7;
    return today.subtract(daysToMonday, 'day').startOf('day');
  });

  const [editScheduleModalOpen, setEditScheduleModalOpen] = useState(false);
  const [deleteScheduleModalOpen, setDeleteScheduleModalOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAskForResponses, setFormAskForResponses] = useState(false);

  const [editShiftModalOpen, setEditShiftModalOpen] = useState(false);
  const [deleteShiftModalOpen, setDeleteShiftModalOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Event | null>(null);
  const [shiftName, setShiftName] = useState('');
  const [shiftDescription, setShiftDescription] = useState('');
  const [shiftLocation, setShiftLocation] = useState('');
  const [shiftStartTime, setShiftStartTime] = useState<Date | undefined>(undefined);
  const [shiftEndTime, setShiftEndTime] = useState<Date | undefined>(undefined);
  const [shiftAssigneeCount, setShiftAssigneeCount] = useState('');
  const [shiftAllowSelfJoin, setShiftAllowSelfJoin] = useState(false);
  const [shiftAllowShiftExchangeRequests, setShiftAllowShiftExchangeRequests] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState<SelectedAssignee[]>([]);
  const [assigneeAttendance, setAssigneeAttendance] = useState<Record<string, EventParticipationAnswer>>({});
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [isAssigneeInputFocused, setIsAssigneeInputFocused] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [attendanceUpdatingKey, setAttendanceUpdatingKey] = useState<string | null>(null);
  const [exportingShifts, setExportingShifts] = useState(false);
  const [linkedScheduleCommunities, setLinkedScheduleCommunities] = useState<ScheduleCommunityModel[]>([]);
  const [shiftCommunityAssignments, setShiftCommunityAssignments] = useState<DraftShiftCommunityAssignment[]>([]);
  const [shiftAssignmentMode, setShiftAssignmentMode] = useState<ShiftAssignmentMode>('members');

  const [saving, setSaving] = useState(false);

  const useCommunitiesMode = isMainBusinessCommunity && shiftAssignmentMode === 'communities';
  const useMembersMode = !isMainBusinessCommunity || shiftAssignmentMode === 'members';

  const invalidShiftEndTime =
    !!shiftStartTime && !!shiftEndTime && new Date(shiftEndTime) <= new Date(shiftStartTime);
  const parsedShiftCapacity =
    shiftAssigneeCount.trim() === '' ? undefined : Number(shiftAssigneeCount);
  const hasValidShiftAssigneeCount =
    parsedShiftCapacity !== undefined &&
    Number.isInteger(parsedShiftCapacity) &&
    parsedShiftCapacity > 0;
  const hasReachedCapacity =
    useMembersMode && hasValidShiftAssigneeCount && selectedAssignees.length >= parsedShiftCapacity;
  const hasCommunityAssignmentMismatch =
    useCommunitiesMode &&
    shiftCommunityAssignments.length > 0 &&
    hasValidShiftAssigneeCount &&
    getTotalCommunityRequiredCount(shiftCommunityAssignments) !== parsedShiftCapacity;

  const allowSelfJoinLabel = useCommunitiesMode && shiftCommunityAssignments.length > 0
    ? t('page.event.schedules.fields.allowSelfJoinBusiness')
    : t('page.event.schedules.fields.allowSelfJoin');

  const assigneeSuggestions = useMemo(() => {
    const query = assigneeSearch.trim().toLowerCase();

    return connections
      .filter((connection) => !!connection.active)
      .filter((connection) => {
        if (connection.type === ConnectionType.MEMBER) {
          return !!connection.userId;
        }

        return !!connection.externalMemberId;
      })
      .filter((connection) => {
        const selectedKey =
          connection.type === ConnectionType.MEMBER
            ? `member-${connection.userId}`
            : `external-${connection.externalMemberId}`;
        return !selectedAssignees.some((assignee) => assignee.key === selectedKey);
      })
      .filter((connection) => {
        if (!query) return true;
        return getConnectionLabel(connection).toLowerCase().includes(query);
      })
      .sort((a, b) => getConnectionLabel(a).localeCompare(getConnectionLabel(b)))
      .slice(0, 8);
  }, [assigneeSearch, connections, selectedAssignees]);


  useEffect(() => {
    if (scheduleId && community) {
      loadSchedule();
      loadShifts();
    }
  }, [scheduleId, community]);

  useEffect(() => {
    if (isMainBusinessCommunity && schedule) {
      loadLinkedScheduleCommunities();
    }
  }, [isMainBusinessCommunity, schedule?.scheduleId]);

  useEffect(() => {
    if (!isMainBusinessCommunity && activeViewKey === 'communities') {
      setActiveViewKey('list');
    }
  }, [activeViewKey, isMainBusinessCommunity]);

  const loadLinkedScheduleCommunities = async () => {
    if (!schedule) return;

    try {
      const communities = await scheduleService.listScheduleCommunities(schedule.scheduleId);
      setLinkedScheduleCommunities(communities);
    } catch {
      setLinkedScheduleCommunities([]);
    }
  };

  const loadSchedule = async () => {
    if (!scheduleId) return;
    try {
      setLoading(true);
      const schedules = await scheduleService.listSchedules();
      const found = schedules.find((s) => s.scheduleId === Number(scheduleId));
      if (!found) {
        toast.error(t('general.error'));
        navigate('/events', { state: { tab: 'schedules' } });
        return;
      }
      setSchedule(found);
    } catch {
      toast.error(t('general.error'));
      navigate('/events', { state: { tab: 'schedules' } });
    } finally {
      setLoading(false);
    }
  };

  const loadShifts = async () => {
    if (!scheduleId) return;
    try {
      setShiftsLoading(true);
      const data = await scheduleService.listShifts(Number(scheduleId));
      setShifts(data);
    } catch {
      toast.error(t('general.error'));
    } finally {
      setShiftsLoading(false);
    }
  };

  const openEditModal = () => {
    if (!schedule) return;
    setFormName(schedule.name);
    setFormDescription(schedule.description ?? '');
    setFormAskForResponses(schedule.askForResponses);
    setEditScheduleModalOpen(true);
  };

  const handleEditSchedule = async () => {
    if (!schedule || !formName.trim()) return;

    try {
      setSaving(true);
      const updated = await scheduleService.updateSchedule(schedule.scheduleId, {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        askForResponses: formAskForResponses,
      });
      setSchedule(updated);
      setEditScheduleModalOpen(false);
      toast.success(t('page.event.schedules.messages.schedule.updated'));
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!schedule) return;

    try {
      setSaving(true);
      await scheduleService.deleteSchedule(schedule.scheduleId);
      toast.success(t('page.event.schedules.messages.schedule.deleted'));
      navigate('/events', { state: { tab: 'schedules' } });
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSaving(false);
    }
  };

  const loadConnections = async () => {
    try {
      setLoadingConnections(true);
      await connectionService.getConnections();
    } catch {
      toast.error(t('general.error'));
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleShiftAssignmentModeChange = (mode: ShiftAssignmentMode) => {
    setShiftAssignmentMode(mode);
    if (mode === 'communities') {
      setSelectedAssignees([]);
      setAssigneeAttendance({});
      setAssigneeSearch('');
    } else {
      setShiftCommunityAssignments([]);
    }
  };

  const openEditShiftModal = async (shift: Event) => {
    const mappedAssignees = mapShiftAssignees(shift);
    const hasCommunityAssignments = (shift.communityAssignments?.length ?? 0) > 0;
    const assignmentMode: ShiftAssignmentMode = hasCommunityAssignments ? 'communities' : 'members';

    setSelectedShift(shift);
    setShiftName(shift.title);
    setShiftDescription(shift.description || '');
    setShiftLocation(shift.location || '');
    setShiftStartTime(shift.startTime ? new Date(shift.startTime) : undefined);
    setShiftEndTime(shift.endTime ? new Date(shift.endTime) : undefined);
    setShiftAssigneeCount(
      shift.maximumAttendees !== undefined && shift.maximumAttendees !== null
        ? String(shift.maximumAttendees)
        : '',
    );
    setShiftAllowSelfJoin(getShiftAllowSelfJoin(shift));
    setShiftAllowShiftExchangeRequests(getShiftAllowShiftExchangeRequests(shift));
    setShiftAssignmentMode(assignmentMode);
    setSelectedAssignees(assignmentMode === 'members' ? mappedAssignees : []);
    setAssigneeAttendance(
      assignmentMode === 'members'
        ? mappedAssignees.reduce<Record<string, EventParticipationAnswer>>((accumulator, assignee) => {
            accumulator[assignee.key] = assignee.answer ?? EventParticipationAnswer.PENDING;
            return accumulator;
          }, {})
        : {},
    );
    setAssigneeSearch('');
    setShiftCommunityAssignments(
      hasCommunityAssignments ? mapShiftCommunityAssignments(shift.communityAssignments!) : [],
    );
    setEditShiftModalOpen(true);

    if (connections.length === 0) {
      await loadConnections();
    }
  };

  const addAssignee = (connection: Connection) => {
    if (hasReachedCapacity) {
      return;
    }

    if (connection.type === ConnectionType.MEMBER && connection.userId) {
      const key = `member-${connection.userId}`;
      setSelectedAssignees((prev) => [
        ...prev,
        {
          key,
          type: ConnectionType.MEMBER,
          label: getConnectionLabel(connection),
          answer: EventParticipationAnswer.PENDING,
          userId: connection.userId,
        },
      ]);
      setAssigneeAttendance((prev) => ({ ...prev, [key]: EventParticipationAnswer.PENDING }));
      setAssigneeSearch('');
    }

    if (connection.type === ConnectionType.EXTERNAL_MEMBER && connection.externalMemberId) {
      const key = `external-${connection.externalMemberId}`;
      setSelectedAssignees((prev) => [
        ...prev,
        {
          key,
          type: ConnectionType.EXTERNAL_MEMBER,
          label: getConnectionLabel(connection),
          answer: EventParticipationAnswer.PENDING,
          externalMemberId: connection.externalMemberId,
        },
      ]);
      setAssigneeAttendance((prev) => ({ ...prev, [key]: EventParticipationAnswer.PENDING }));
      setAssigneeSearch('');
    }
  };

  const removeAssignee = (key: string) => {
    setSelectedAssignees((prev) => prev.filter((assignee) => assignee.key !== key));
    setAssigneeAttendance((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getAssigneeAttendance = (assignee: SelectedAssignee): EventParticipationAnswer => {
    return assigneeAttendance[assignee.key] ?? assignee.answer ?? EventParticipationAnswer.PENDING;
  };

  const handleUpdateAssigneeAttendance = async (
    assignee: SelectedAssignee,
    answer: EventParticipationAnswer,
  ) => {
    if (!selectedShift) return;

    const participantId =
      assignee.type === ConnectionType.MEMBER ? assignee.userId : assignee.externalMemberId;

    if (!participantId) return;

    const previousAnswer = getAssigneeAttendance(assignee);
    setAssigneeAttendance((prev) => ({ ...prev, [assignee.key]: answer }));

    try {
      setAttendanceUpdatingKey(assignee.key);
      await scheduleService.setShiftAssigneeAttendance(
        selectedShift.id,
        participantId,
        { answer },
        assignee.type === ConnectionType.EXTERNAL_MEMBER,
      );

      setSelectedShift((prev) =>
        prev && prev.id === selectedShift.id ? updateShiftAssigneeAttendance(prev, assignee, answer) : prev,
      );
      setShifts((prev) =>
        prev.map((shift) =>
          shift.id === selectedShift.id ? updateShiftAssigneeAttendance(shift, assignee, answer) : shift,
        ),
      );
      toast.success(t('page.event.schedules.attendance.updated'));
    } catch {
      setAssigneeAttendance((prev) => ({ ...prev, [assignee.key]: previousAnswer }));
      toast.error(t('general.error'));
    } finally {
      setAttendanceUpdatingKey(null);
    }
  };

  const handleAssigneeInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (assigneeSearch.trim().length > 0 && assigneeSuggestions.length > 0 && !hasReachedCapacity) {
        event.preventDefault();
        addAssignee(assigneeSuggestions[0]);
      }
      return;
    }

    if (event.key === 'Backspace' && assigneeSearch.length === 0 && selectedAssignees.length > 0) {
      event.preventDefault();
      const previousAssignee = selectedAssignees[selectedAssignees.length - 1];
      removeAssignee(previousAssignee.key);
    }
  };

  const handleSaveShift = async () => {
    if (
      !schedule ||
      !selectedShift ||
      !shiftName.trim() ||
      !shiftStartTime ||
      invalidShiftEndTime ||
      !hasValidShiftAssigneeCount
    ) {
      return;
    }

    if (useMembersMode && selectedAssignees.length > parsedShiftCapacity) {
      toast.error(t('page.event.schedules.capacityReached'));
      return;
    }

    if (hasCommunityAssignmentMismatch) {
      toast.error(t('page.event.schedules.communities.quotaMismatch'));
      return;
    }

    try {
      setSaving(true);

      const communityAssignments = useCommunitiesMode
        ? getCommunityAssignmentsPayload(shiftCommunityAssignments)
        : [];

      const updateBody: UpdateShiftBody = {
        name: shiftName.trim(),
        description: shiftDescription,
        location: shiftLocation.trim() || undefined,
        startTime: shiftStartTime.toISOString(),
        endTime: shiftEndTime ? shiftEndTime.toISOString() : null,
        assigneeCount: parsedShiftCapacity,
        allowSelfJoin: shiftAllowSelfJoin,
        allowShiftExchangeRequests: shiftAllowShiftExchangeRequests,
        communityAssignments: isMainBusinessCommunity ? communityAssignments : undefined,
      };

      await scheduleService.updateShift(schedule.scheduleId, selectedShift.id, updateBody);

      const shouldUpdateMemberAssignments =
        useMembersMode ||
        selectedShift.participants.length > 0 ||
        selectedShift.externalInvites.length > 0;

      if (shouldUpdateMemberAssignments) {
        await scheduleService.setShiftAssignments(schedule.scheduleId, selectedShift.id, {
          participantsIds: useMembersMode
            ? selectedAssignees
                .filter((assignee) => assignee.type === ConnectionType.MEMBER && assignee.userId)
                .map((assignee) => assignee.userId as number)
            : [],
          externalInvites: useMembersMode
            ? selectedAssignees
                .filter(
                  (assignee) =>
                    assignee.type === ConnectionType.EXTERNAL_MEMBER && assignee.externalMemberId,
                )
                .map((assignee) => ({ externalMemberId: assignee.externalMemberId as number }))
            : [],
        });
      }

      await loadShifts();
      setEditShiftModalOpen(false);
      setSelectedShift(null);
      toast.success(t('page.event.schedules.messages.shift.updated'));
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShift = async (
    deleteRecurrences: RecurrenceEditType = RecurrenceEditType.SINGLE,
  ) => {
    if (!schedule || !selectedShift) return;

    try {
      setSaving(true);
      await scheduleService.deleteShift(schedule.scheduleId, selectedShift.id, deleteRecurrences);
      await loadShifts();
      setDeleteShiftModalOpen(false);
      setEditShiftModalOpen(false);
      setSelectedShift(null);
      toast.success(t('page.event.schedules.messages.shift.deleted'));
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleExportShifts = async () => {
    if (!schedule) return;

    if (shifts.length === 0) {
      toast.info(t('page.event.schedules.messages.shift.exportNoShifts'));
      return;
    }

    try {
      setExportingShifts(true);
      downloadScheduleShiftsExcel(shifts, schedule.name);
      toast.success(t('page.event.schedules.messages.shift.exported'));
    } catch {
      toast.error(t('general.error'));
    } finally {
      setExportingShifts(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <ScheduleListSkeleton />
      </div>
    );
  }

  if (!schedule) {
    return <div />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate('/events', { state: { tab: 'schedules' } })}
            variant="tertiary">
            <FaArrowLeft className="size-5" />
            <span>{t('general.back')}</span>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary-outline"
            onClick={handleExportShifts}
            loading={exportingShifts}
            disabled={shiftsLoading || shifts.length === 0}
            data-testid="schedule-export-button">
            <TbDownload className="h-4 w-4" />
            {t('page.event.schedules.export.button')}
          </Button>
          <Button
            variant="danger-v2"
            onClick={() => setDeleteScheduleModalOpen(true)}>
            <FaTrash className="h-3.5 w-3.5" />
            {t('general.delete')}
          </Button>
          <Button
            variant="secondary"
            onClick={openEditModal}>
            <FaEdit className="h-3.5 w-3.5" />
            {t('general.edit')}
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/events/schedule/${schedule.scheduleId}/shifts/create`)}>
            <FaPlus className="h-4 w-4" />
            {t('page.event.schedules.createShift')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-semibold text-primary-900">{schedule.name}</h1>
          {schedule.description && (
            <p className="text-sm text-primary-500">{schedule.description}</p>
          )}
        </div>
      </div>

      <PageWithTabsLayout
        tabs={visibleTabs}
        activeTab={viewLabels[activeViewKey] ?? viewLabels.list ?? ''}
        setActiveTab={(tab) => {
          const newKey = (Object.keys(viewLabels) as ViewKey[]).find(
            (key) => viewLabels[key] === tab,
          );
          if (newKey) setActiveViewKey(newKey);
        }}
        renderTabContent={(tab) =>
          shiftsLoading ? (
            <ScheduleListSkeleton />
          ) : tab === viewLabels.calendar ? (
            <ScheduleWeekCalendar
              shifts={shifts}
              selectedWeekStart={selectedWeekStart}
              onPrevWeek={() => setSelectedWeekStart((week) => week.subtract(7, 'day'))}
              onNextWeek={() => setSelectedWeekStart((week) => week.add(7, 'day'))}
              language={i18n.language}
              t={t}
              onShiftClick={openEditShiftModal}
            />
          ) : tab === viewLabels.communities ? (
            <ScheduleCommunitiesTab
              schedule={schedule}
              onCommunitiesUpdated={async () => {
                await loadLinkedScheduleCommunities();
              }}
            />
          ) : tab === viewLabels.fastAssigning ? (
            <FastAssigningTab
              schedule={schedule}
              shifts={shifts}
              linkedCommunities={linkedScheduleCommunities}
              isMainBusinessCommunity={isMainBusinessCommunity}
              onAssignmentsApplied={loadShifts}
            />
          ) : tab === viewLabels.connectEvents ? (
            <ScheduleConnectEventsTab
              schedule={schedule}
              shifts={shifts}
              onConnectionsApplied={loadShifts}
            />
          ) : tab === viewLabels.insights ? (
            <ScheduleInsightsTab
              shifts={shifts}
              t={t}
            />
          ) : shifts.length === 0 ? (
            <EmptyScreenView subtitle={t('page.event.schedules.emptyShifts')} />
          ) : (
            <ScheduleShiftsList shifts={shifts} onShiftClick={openEditShiftModal} t={t} />
          )
        }
      />

      <ModalLayout
        closeButton
        open={editScheduleModalOpen}
        setOpen={setEditScheduleModalOpen}
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
        <div className="flex flex-col gap-4">
          <h4 className="text-lg font-semibold text-primary-900">
            {t('page.event.schedules.editSchedule')}
          </h4>
          <Input
            id="schedule-edit-name"
            label={t('page.event.schedules.fields.scheduleName')}
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            required
          />
          <Input
            id="schedule-edit-description"
            label={t('page.event.schedules.fields.scheduleDescription')}
            type="textarea"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
          />
          <Slider
            label={t('page.event.schedules.fields.askForResponses')}
            items={[
              { label: t('general.no'), value: false },
              { label: t('general.yes'), value: true },
            ]}
            value={formAskForResponses}
            onChange={setFormAskForResponses}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditScheduleModalOpen(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleEditSchedule}
              disabled={!formName.trim() || saving}>
              {t('general.save')}
            </Button>
          </div>
        </div>
      </ModalLayout>

      <ModalLayout
        closeButton
        open={deleteScheduleModalOpen}
        setOpen={setDeleteScheduleModalOpen}
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
        <h4 className="text-lg font-semibold text-primary-900">
          {t('page.event.schedules.deleteSchedule')}
        </h4>
        <p className="text-primary-600">
          {t('page.event.schedules.deleteConfirmation', { name: schedule.name })}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteScheduleModalOpen(false)}>
            {t('general.cancel')}
          </Button>
          <Button variant="danger-v2" onClick={handleDeleteSchedule} disabled={saving}>
            {t('general.delete')}
          </Button>
        </div>
      </ModalLayout>

      <ModalLayout
        closeButton
        open={editShiftModalOpen}
        setOpen={setEditShiftModalOpen}
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[760px]">
        <div className="flex flex-col gap-4">
        <h4 className="text-lg font-semibold text-primary-900">
          {t('page.event.schedules.editShift')}
        </h4>
        <Input
          id="shift-edit-name"
          label={t('page.event.schedules.fields.shiftName')}
          type="text"
          value={shiftName}
          onChange={(e) => setShiftName(e.target.value)}
          required
        />
        <Input
          id="shift-edit-description"
          label={t('page.event.schedules.fields.shiftDescription')}
          type="textarea"
          value={shiftDescription}
          onChange={(e) => setShiftDescription(e.target.value)}
        />
        <Input
          id="shift-edit-location"
          label={t('page.event.schedules.fields.shiftLocation')}
          type="text"
          value={shiftLocation}
          onChange={(e) => setShiftLocation(e.target.value)}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-primary-700">
              {t('page.event.schedules.fields.startDate')} *
            </label>
            <DatePicker date={shiftStartTime} setDate={setShiftStartTime} includeTime />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-primary-700">
              {t('page.event.schedules.fields.endDate')}
            </label>
            <DatePicker date={shiftEndTime} setDate={setShiftEndTime} includeTime />
          </div>
        </div>
        <Input
          id="shift-edit-assignee-count"
          label={t('page.event.schedules.fields.assigneeCount')}
          type="number"
          value={shiftAssigneeCount}
          onChange={(e) => setShiftAssigneeCount(e.target.value)}
          min={1}
          required
        />
        <Slider
          label={allowSelfJoinLabel}
          items={[
            { label: t('general.no'), value: false },
            { label: t('general.yes'), value: true },
          ]}
          value={shiftAllowSelfJoin}
          onChange={setShiftAllowSelfJoin}
        />
        <Slider
          label={t('page.event.schedules.fields.allowShiftExchangeRequests')}
          items={[
            { label: t('general.no'), value: false },
            { label: t('general.yes'), value: true },
          ]}
          value={shiftAllowShiftExchangeRequests}
          onChange={setShiftAllowShiftExchangeRequests}
        />

        {isMainBusinessCommunity && (
          <Slider
            label={t('page.event.schedules.fastAssigning.mode')}
            items={[
              {
                label: t('page.event.schedules.fastAssigning.modes.members'),
                value: 'members' as ShiftAssignmentMode,
              },
              {
                label: t('page.event.schedules.fastAssigning.modes.communities'),
                value: 'communities' as ShiftAssignmentMode,
              },
            ]}
            value={shiftAssignmentMode}
            onChange={handleShiftAssignmentModeChange}
          />
        )}

        {useCommunitiesMode && (
          <ShiftCommunityAssignmentsEditor
            linkedCommunities={linkedScheduleCommunities}
            assignments={shiftCommunityAssignments}
            onChange={setShiftCommunityAssignments}
          />
        )}

        {hasCommunityAssignmentMismatch && (
          <p className="text-sm text-danger">{t('page.event.schedules.communities.quotaMismatch')}</p>
        )}

        {useMembersMode && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-primary-900">
                {t('page.event.schedules.assignMembers')}
              </p>
              <p className="text-xs text-primary-500">
                {t('page.event.schedules.selectedAssignees', {
                  count: selectedAssignees.length,
                })}
              </p>
            </div>
            {hasReachedCapacity && (
              <span className="rounded-full bg-primary-900 px-2 py-1 text-xs text-secondary-50">
                {t('page.event.schedules.capacityReached')}
              </span>
            )}
          </div>

          <div className="relative">
            <div className="flex min-h-[48px] flex-wrap items-center rounded-xl border-2 border-secondary-200 bg-secondary-200 bg-opacity-50">
              {selectedAssignees.map((assignee) => (
                <span
                  key={assignee.key}
                  className="inline-flex items-center gap-2 rounded-full bg-secondary-200 px-3 py-1 m-2 text-sm text-primary-900">
                  <span>{assignee.label}</span>
                  <button
                    type="button"
                    onClick={() => removeAssignee(assignee.key)}
                    className="text-primary-500 transition-colors hover:text-primary-900">
                    <FaTimes className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                value={assigneeSearch}
                onChange={(e) => setAssigneeSearch(e.target.value)}
                onKeyDown={handleAssigneeInputKeyDown}
                onFocus={() => {
                  setIsAssigneeInputFocused(true);
                  if (connections.length === 0) loadConnections();
                }}
                onBlur={() => {
                  setTimeout(() => setIsAssigneeInputFocused(false), 120);
                }}
                placeholder={t('page.event.schedules.searchMembersPlaceholder')}
                className="min-w-[180px] flex-1 bg-transparent border-none text-sm  text-primary-900 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {!hasReachedCapacity && isAssigneeInputFocused && (
              <div className="absolute z-10 mt-2 max-h-64 w-full overflow-auto rounded-xl bg-secondary-50 shadow-lg">
                {loadingConnections ? (
                  <div className="px-3 py-2 text-sm text-primary-500">{t('general.loading')}</div>
                ) : assigneeSuggestions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-primary-500">
                    {t('page.event.schedules.noMembersFound')}
                  </div>
                ) : (
                  assigneeSuggestions.slice(0, 5).map((connection) => (
                    <button
                      key={
                        connection.type === ConnectionType.MEMBER
                          ? `member-${connection.userId}`
                          : `external-${connection.externalMemberId}`
                      }
                      type="button"
                      onClick={() => addAssignee(connection)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-primary-900 transition-colors hover:bg-secondary-200">
                      <span>{getConnectionLabel(connection)}</span>
                      <span className="text-xs text-primary-400">
                        {connection.type === ConnectionType.EXTERNAL_MEMBER ? 'External' : 'Member'}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-secondary-200 bg-secondary-100 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-primary-900">
                {t('page.event.schedules.attendance.title')}
              </p>
              {attendanceUpdatingKey && (
                <span className="text-xs text-primary-500">{t('general.loading')}</span>
              )}
            </div>

            {selectedAssignees.length === 0 ? (
              <p className="text-xs text-primary-500">
                {t('page.event.schedules.attendance.empty')}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {selectedAssignees.map((assignee) => {
                  const currentAnswer = getAssigneeAttendance(assignee);
                  const disabled = attendanceUpdatingKey === assignee.key;

                  return (
                    <div
                      key={`${assignee.key}-attendance-row`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-secondary-50 px-3 py-2">
                      <span className="text-sm text-primary-900">{assignee.label}</span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={
                            currentAnswer === EventParticipationAnswer.YES ? 'primary' : 'primary-outline'
                          }
                          className={`h-8 px-3 ${currentAnswer === EventParticipationAnswer.YES && 'bg-success'}`}
                          onClick={() =>
                            handleUpdateAssigneeAttendance(assignee, EventParticipationAnswer.YES)
                          }
                          disabled={disabled}>
                          {t('page.event.schedules.attendance.showedUp')}
                        </Button>
                        <Button
                          variant={
                            currentAnswer === EventParticipationAnswer.NO ? 'danger-v2' : 'primary-outline'
                          }
                          className="h-8 px-3"
                          onClick={() =>
                            handleUpdateAssigneeAttendance(assignee, EventParticipationAnswer.NO)
                          }
                          disabled={disabled}>
                          {t('page.event.schedules.attendance.noShow')}
                        </Button>
                        <Button
                          variant={
                            currentAnswer === EventParticipationAnswer.PENDING
                              ? 'secondary'
                              : 'primary-outline'
                          }
                          className="h-8 px-3"
                          onClick={() =>
                            handleUpdateAssigneeAttendance(assignee, EventParticipationAnswer.PENDING)
                          }
                          disabled={disabled}>
                          {t('page.event.schedules.attendance.unknown')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="danger-v2" onClick={() => setDeleteShiftModalOpen(true)} disabled={saving}>
            {t('general.delete')}
          </Button>
          <Button variant="secondary" onClick={() => setEditShiftModalOpen(false)}>
            {t('general.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveShift}
            disabled={
              !shiftName.trim() ||
              !shiftStartTime ||
              !hasValidShiftAssigneeCount ||
              invalidShiftEndTime ||
              hasCommunityAssignmentMismatch ||
              (useMembersMode && selectedAssignees.length > (parsedShiftCapacity ?? 0)) ||
              !!attendanceUpdatingKey ||
              saving
            }>
            {t('general.save')}
          </Button>
        </div>
        </div>
      </ModalLayout>

      <ModalLayout
        closeButton
        open={deleteShiftModalOpen}
        setOpen={setDeleteShiftModalOpen}
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
        <div className="flex min-w-[280px] flex-col gap-4">
          <h4 className="text-lg font-semibold text-primary-900">
            {selectedShift?.isRecurring
              ? t('page.event.schedules.deleteRecurringShift.title')
              : t('page.event.schedules.deleteShift')}
          </h4>

          {selectedShift?.isRecurring ? (
            <>
              <p className="text-primary-600">{t('page.event.schedules.deleteRecurringShift.description')}</p>
              {Object.values(RecurrenceEditType).map((recurrenceEditType) => (
                <Button
                  key={recurrenceEditType}
                  variant="primary"
                  className="w-full justify-center"
                  onClick={() => handleDeleteShift(recurrenceEditType)}
                  disabled={saving}>
                  {t(`page.event.schedules.shiftRecurrenceDeleteOptions.${recurrenceEditType.toLowerCase()}`)}
                </Button>
              ))}
              <Button
                variant="secondary"
                className="w-full justify-center"
                onClick={() => setDeleteShiftModalOpen(false)}>
                {t('general.cancel')}
              </Button>
            </>
          ) : (
            <>
              <p className="text-primary-600">
                {t('page.event.schedules.deleteShiftConfirmation', {
                  name: selectedShift?.title || '',
                })}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setDeleteShiftModalOpen(false)}>
                  {t('general.cancel')}
                </Button>
                <Button variant="danger" onClick={() => handleDeleteShift()} disabled={saving}>
                  {t('general.delete')}
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalLayout>
    </div>
  );
}

function ScheduleListSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="h-16 rounded-lg border border-primary-100 bg-primary-50 animate-pulse"
        />
      ))}
    </div>
  );
}
